import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../hooks/useAuth";

const INTERESES = [
  { id: "ansiedad",           label: "Ansiedad",          emoji: "😮‍💨" },
  { id: "depresion",          label: "Depresión",         emoji: "💙" },
  { id: "autoestima",         label: "Autoestima",        emoji: "🌸" },
  { id: "relaciones",         label: "Relaciones",        emoji: "❤️" },
  { id: "estres",             label: "Estrés",            emoji: "🌊" },
  { id: "duelo",              label: "Duelo",             emoji: "🕊️" },
  { id: "trauma",             label: "Trauma",            emoji: "🛡️" },
  { id: "crecimiento_personal", label: "Crecimiento",    emoji: "🌱" },
  { id: "otro",               label: "Otro",              emoji: "✨" },
] as const;

const NIVEL_LABELS: Record<number, string> = {
  1: "Muy poco",
  2: "Algo",
  3: "Moderado",
  4: "Bastante",
  5: "Mucho",
};

type InteresId = typeof INTERESES[number]["id"];

export default function Perfilado() {
  const navigate = useNavigate();
  const { token } = useAuth();

  const [interes,  setInteres]  = useState<InteresId | null>(null);
  const [objetivo, setObjetivo] = useState("");
  const [nivel,    setNivel]    = useState<number | null>(null);
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState<string | null>(null);

  const progress = [interes, objetivo.trim().length >= 5, nivel].filter(Boolean).length;

  async function handleSubmit() {
    if (!interes || !objetivo.trim() || !nivel) {
      setError("Por favor completa todos los campos antes de continuar.");
      return;
    }
    if (!token) {
      navigate("/registro");
      return;
    }

    setError(null);
    setLoading(true);

    const result = await api.saveProfile(
      { interes, objetivo: objetivo.trim(), nivel_afectacion: nivel },
      token
    );

    setLoading(false);

    if (!result.ok) {
      setError(result.error.message);
      return;
    }

    navigate("/dashboard");
  }

  return (
    <div className="perfilado-page">
      <div className="perfilado-card">
        {/* Progress */}
        <div className="perfilado-progress">
          <div
            className="perfilado-progress__bar"
            style={{ width: `${(progress / 3) * 100}%` }}
          />
        </div>

        {/* Header */}
        <div className="perfilado-header">
          <div className="perfilado-header__emoji">🧭</div>
          <h1 className="perfilado-header__title">Cuéntanos sobre ti</h1>
          <p className="perfilado-header__subtitle">
            Esto nos ayuda a personalizar tu experiencia. Solo toma 1 minuto.
          </p>
        </div>

        {error && (
          <div className="alert alert--error" style={{ marginBottom: "1.5rem" }} role="alert">
            ⚠ {error}
          </div>
        )}

        {/* Paso 1: Interés */}
        <div className="perfilado-step">
          <p className="perfilado-step__label">1. ¿Cuál es tu interés principal?</p>
          <p className="perfilado-step__hint">Puedes enfocarte en más áreas después</p>
          <div className="interest-grid">
            {INTERESES.map((item) => (
              <button
                key={item.id}
                type="button"
                className={`interest-btn${interes === item.id ? " interest-btn--active" : ""}`}
                onClick={() => setInteres(item.id)}
              >
                <span className="interest-btn__emoji">{item.emoji}</span>
                {item.label}
              </button>
            ))}
          </div>
        </div>

        {/* Paso 2: Objetivo */}
        <div className="perfilado-step">
          <label className="perfilado-step__label" htmlFor="objetivo">
            2. ¿Qué quieres lograr?
          </label>
          <p className="perfilado-step__hint">
            En tus palabras, ¿cómo sería tu vida si mejoraras esto?
          </p>
          <textarea
            id="objetivo"
            className="form-input"
            placeholder="Ej: Quiero dejar de sentir esa ansiedad constante y poder disfrutar mi día sin estar en alerta..."
            value={objetivo}
            onChange={(e) => setObjetivo(e.target.value)}
            rows={3}
          />
        </div>

        {/* Paso 3: Nivel */}
        <div className="perfilado-step">
          <p className="perfilado-step__label">
            3. ¿Cuánto afecta esto tu vida diaria?
          </p>
          <p className="perfilado-step__hint">1 = muy poco · 5 = muchísimo</p>
          <div className="nivel-row">
            {[1, 2, 3, 4, 5].map((n) => (
              <button
                key={n}
                type="button"
                className={`nivel-btn${nivel === n ? " nivel-btn--active" : ""}`}
                onClick={() => setNivel(n)}
                title={NIVEL_LABELS[n]}
              >
                {n}
              </button>
            ))}
          </div>
          {nivel && (
            <div className="nivel-labels">
              <span>{NIVEL_LABELS[1]}</span>
              <span style={{ color: "var(--copper)", fontWeight: 600 }}>
                {NIVEL_LABELS[nivel]}
              </span>
              <span>{NIVEL_LABELS[5]}</span>
            </div>
          )}
        </div>

        <button
          type="button"
          className="btn btn-primary btn--full btn--lg"
          onClick={handleSubmit}
          disabled={loading || !interes || objetivo.trim().length < 5 || !nivel}
        >
          {loading ? (
            <><span className="spinner" /> Guardando...</>
          ) : (
            "Comenzar mi camino →"
          )}
        </button>
      </div>
    </div>
  );
}
